import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const INTERACTION_TYPE_LABELS: Record<string, string> = {
  call: 'Phone Call',
  text: 'Text',
  in_person: 'In-Person',
  video: 'Video',
};

export function formatInteractionType(type: string): string {
  return (
    INTERACTION_TYPE_LABELS[type] ||
    type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function formatPhoneNumber(phoneNumberString: string): string {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  // US 10-digit: 4156785432 → (415) 678-5432
  const match10 = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match10) {
    return `(${match10[1]}) ${match10[2]}-${match10[3]}`;
  }
  // US 11-digit with country code: 14156785432 → +1 (415) 678-5432
  const match11 = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
  if (match11) {
    return `+1 (${match11[1]}) ${match11[2]}-${match11[3]}`;
  }
  return phoneNumberString;
}

// ─── Date Formatting ────────────────────────────────────────────────────────

/** Formats an ISO date string as "Mar 18, 2026". Returns empty string for falsy input. */
export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  // Detect date-only values: bare "YYYY-MM-DD" or midnight-UTC timestamps.
  // Parse the date portion as local to avoid timezone shift.
  const dateOnly = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(T00:00:00(.000)?Z?)?$/);
  let d: Date;
  if (dateOnly) {
    d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
  } else {
    d = new Date(dateStr);
  }
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Returns the local calendar date (YYYY-MM-DD) for a given Date object.
 * Avoids UTC vs local timezone mismatches.
 */
export function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Returns a human-readable relative time string, comparing calendar days in
 * the user's local timezone (not raw timestamp diff which causes off-by-one).
 */
export function timeAgo(dateStr: string): string {
  const now = new Date();

  // Detect date-only values: bare "YYYY-MM-DD" or midnight-UTC timestamps
  // ("2026-03-22T00:00:00.000Z"). Both represent a calendar date, not a precise
  // moment, so parse the date portion as local to avoid timezone shift.
  const dateOnly = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(T00:00:00(.000)?Z?)?$/);
  let date: Date;
  const isDateOnly = !!dateOnly;
  if (dateOnly) {
    date = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
  } else {
    date = new Date(dateStr);
  }

  // Compare local calendar dates to avoid timezone drift
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const daysDiff = Math.round((todayLocal.getTime() - dateLocal.getTime()) / (1000 * 60 * 60 * 24));

  if (daysDiff <= 0) {
    // Date-only inputs: just say "Today" since we don't track time
    if (isDateOnly) return 'Today';
    // Full timestamps: show relative time
    const diffMs = now.getTime() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ago`;
  }
  if (daysDiff === 1) return 'Yesterday';
  if (daysDiff < 7) return `${daysDiff}d ago`;
  if (daysDiff < 30) return `${Math.floor(daysDiff / 7)}w ago`;
  return formatDate(dateStr);
}

// ─── Web Share API ──────────────────────────────────────────────────────────

export function canShare(): boolean {
  return 'share' in navigator;
}

export async function shareApp(referralCode?: string): Promise<boolean> {
  if (!canShare()) return false;
  const url = referralCode
    ? `https://app.trynudge.xyz?ref=${referralCode}`
    : 'https://app.trynudge.xyz';
  try {
    await navigator.share({
      title: 'Nudge — Stay Connected',
      text: 'Nudge helps you stay in touch with the people who matter. Try it out!',
      url,
    });
    return true;
  } catch {
    return false; // User cancelled or share failed
  }
}

export async function shareContact(name: string): Promise<boolean> {
  if (!canShare()) return false;
  try {
    await navigator.share({
      title: `${name} on Nudge`,
      text: `I'm using Nudge to stay in touch with ${name} and other important people. Try it!`,
      url: 'https://app.trynudge.xyz',
    });
    return true;
  } catch {
    return false;
  }
}

// ─── Badge API ──────────────────────────────────────────────────────────────

export async function setBadgeCount(count: number): Promise<void> {
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    } catch {
      // Badge API not supported or permission denied
    }
  }
}

export async function clearBadge(): Promise<void> {
  if ('clearAppBadge' in navigator) {
    try {
      await navigator.clearAppBadge();
    } catch {
      // ignore
    }
  }
}
